import { Icon } from '@iconify/react';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, SectionTitle } from '../../shared/components';
import { ColorChip, PriorityChip } from '../components/Chips';
import TaskFormDialog from '../components/TaskFormDialog';
import { useTaskMeta } from '../components/useTaskMeta';
import { tasksApi, TaskListParams } from '../tasks.api';
import type { Page, TaskSummary } from '../tasks.types';
import { errorMessage, fmtDate, fmtHours } from '../tasks.utils';

const SORTABLE: { key: string; label: string }[] = [
  { key: 'taskCode', label: 'Task' },
  { key: 'priority', label: 'Priority' },
  { key: 'status', label: 'Status' },
  { key: 'dueDate', label: 'Due' },
  { key: 'allocatedHours', label: 'Allocated' },
  { key: 'actualHours', label: 'Actual' },
];

/** Task list: search, filter, sort, page. Managers see everyone's work; employees their own. */
export default function TaskListPage() {
  const navigate = useNavigate();
  const meta = useTaskMeta();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<Page<TaskSummary> | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const q = {
    search: params.get('search') ?? '',
    status: params.get('status') ?? '',
    priority: params.get('priority') ?? '',
    employeeId: params.get('employeeId') ?? '',
    projectId: params.get('projectId') ?? '',
    departmentId: params.get('departmentId') ?? '',
    overdue: params.get('overdue') === 'true',
    includeClosed: params.get('includeClosed') === 'true',
    page: Number(params.get('page') ?? 0),
    size: Number(params.get('size') ?? 20),
    sort: params.get('sort') ?? 'dueDate,asc',
  };
  const setQ = (patch: Record<string, string | number | boolean | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v === null || v === '' || v === false ? next.delete(k) : next.set(k, String(v))));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };

  const canViewAll = Boolean(meta?.permissions.TASK_VIEW_ALL);
  const key = params.toString();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p: TaskListParams = {
        search: q.search || undefined,
        status: q.status ? [q.status] : undefined,
        priority: q.priority || undefined,
        employeeId: q.employeeId ? Number(q.employeeId) : undefined,
        projectId: q.projectId ? Number(q.projectId) : undefined,
        departmentId: q.departmentId ? Number(q.departmentId) : undefined,
        overdue: q.overdue || undefined,
        includeClosed: q.includeClosed || undefined,
        page: q.page,
        size: q.size,
        sort: q.sort,
      };
      setData(await tasksApi.list(p));
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to load tasks'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    load();
  }, [load]);

  const [sortKey, sortDir] = q.sort.split(',');
  const sortBy = (k: string) => setQ({ sort: `${k},${sortKey === k && sortDir === 'asc' ? 'desc' : 'asc'}` });

  return (
    <Stack gap={3}>
      <SectionTitle
        title='TASKS'
        action={
          meta?.permissions.TASK_CREATE && (
            <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} onClick={() => setCreating(true)}>
              New task
            </Button>
          )
        }
      />
      <Card sx={{ py: 2 }}>
        <Stack direction='row' gap={2} flexWrap='wrap' alignItems='center'>
          <TextField size='small' label='Search code, title, project' sx={{ minWidth: 240 }} defaultValue={q.search}
            onKeyDown={(e) => e.key === 'Enter' && setQ({ search: (e.target as HTMLInputElement).value })}
            onBlur={(e) => e.target.value !== q.search && setQ({ search: e.target.value })} />
          <TextField select size='small' label='Status' sx={{ minWidth: 150 }} value={q.status} onChange={(e) => setQ({ status: e.target.value })}>
            <MenuItem value=''>All open</MenuItem>
            {meta?.statuses.map((s) => (
              <MenuItem key={s.id} value={s.code ?? ''}>{s.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size='small' label='Priority' sx={{ minWidth: 130 }} value={q.priority} onChange={(e) => setQ({ priority: e.target.value })}>
            <MenuItem value=''>Any</MenuItem>
            {meta?.priorities.map((p) => (
              <MenuItem key={p.id} value={p.code ?? ''}>{p.label}</MenuItem>
            ))}
          </TextField>
          {canViewAll && (
            <>
              <TextField select size='small' label='Employee' sx={{ minWidth: 180 }} value={q.employeeId} onChange={(e) => setQ({ employeeId: e.target.value })}>
                <MenuItem value=''>Anyone</MenuItem>
                {meta?.employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>{e.name}</MenuItem>
                ))}
              </TextField>
              <TextField select size='small' label='Department' sx={{ minWidth: 170 }} value={q.departmentId} onChange={(e) => setQ({ departmentId: e.target.value })}>
                <MenuItem value=''>All</MenuItem>
                {meta?.departments.map((d) => (
                  <MenuItem key={d.id} value={String(d.id)}>{d.label}</MenuItem>
                ))}
              </TextField>
            </>
          )}
          <TextField select size='small' label='Project' sx={{ minWidth: 200 }} value={q.projectId} onChange={(e) => setQ({ projectId: e.target.value })}>
            <MenuItem value=''>All</MenuItem>
            {meta?.projects.map((p) => (
              <MenuItem key={p.id} value={String(p.id)}>{p.code} — {p.name}</MenuItem>
            ))}
          </TextField>
          <FormControlLabel control={<Switch checked={q.overdue} onChange={(e) => setQ({ overdue: e.target.checked })} />} label='Overdue' />
          <FormControlLabel control={<Switch checked={q.includeClosed} onChange={(e) => setQ({ includeClosed: e.target.checked })} />} label='Include closed' />
        </Stack>
      </Card>

      <Card sx={{ p: 0, py: 0 }}>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {/* The project a task is for leads, as on My Work; the task code is not shown. */}
                <TableCell>Project / task</TableCell>
                <TableCell>Assignee</TableCell>
                {SORTABLE.slice(1).map((c) => (
                  <TableCell key={c.key} align={c.key.endsWith('Hours') ? 'right' : 'left'}>
                    <TableSortLabel active={sortKey === c.key} direction={sortDir as 'asc' | 'desc'} onClick={() => sortBy(c.key)}>{c.label}</TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align='right'>Remaining</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.content.map((t) => (
                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tasks/${t.id}`)}>
                  <TableCell>
                    <Typography variant='body2' fontWeight={600}>
                      {[t.projectCode, t.projectName].filter(Boolean).join(' - ')}
                      {t.projectLocation ? `, ${t.projectLocation}` : ''}
                    </Typography>
                    <Typography variant='body2'>{t.title}</Typography>
                    {t.workActivityName && (
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {t.workActivityName}
                        {t.typeOfWorkActivityName && ` · ${t.typeOfWorkActivityName}`}
                        {t.structuralWorkType && ` · ${t.structuralWorkType.charAt(0)}${t.structuralWorkType.slice(1).toLowerCase()}`}
                        {t.structuralDrawings?.length > 0 && ` · ${t.structuralDrawings.join(', ')}`}
                      </Typography>
                    )}
                    {t.subtaskCount > 0 && <Typography variant='caption' color='text.secondary'>{t.subtaskCount} subtask(s)</Typography>}
                  </TableCell>
                  <TableCell>{t.assigneeName ?? <Typography variant='body2' color='text.secondary'>Unassigned</Typography>}</TableCell>
                  <TableCell><PriorityChip code={t.priority} label={t.priorityLabel} fallbackColor={t.priorityColor} /></TableCell>
                  <TableCell><ColorChip label={t.statusLabel} color={t.statusColor} /></TableCell>
                  <TableCell sx={{ color: t.overdue ? 'error.main' : undefined, whiteSpace: 'nowrap' }}>
                    {fmtDate(t.dueDate)}{t.overdue && ' · overdue'}
                  </TableCell>
                  <TableCell align='right'>{fmtHours(t.allocatedHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(t.actualHours)}</TableCell>
                  <TableCell align='right'>{fmtHours(t.remainingHours)}</TableCell>
                </TableRow>
              ))}
              {data && data.content.length === 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: 8 } as object)} align='center' sx={{ py: 4, color: 'text.secondary' }}>No tasks match these filters.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component='div'
          count={data?.totalElements ?? 0}
          page={q.page}
          rowsPerPage={q.size}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(_, p) => setQ({ page: p })}
          onRowsPerPageChange={(e) => setQ({ size: Number(e.target.value), page: 0 })}
        />
      </Card>

      {meta && (
        <TaskFormDialog
          open={creating}
          meta={meta}
          defaultProjectId={q.projectId ? Number(q.projectId) : null}
          onClose={() => setCreating(false)}
          onSaved={(id) => navigate(`/tasks/${id}`)}
        />
      )}
    </Stack>
  );
}
